import { buildQueryString } from '@/helpers/buildQueryString';
import { api } from '@/services/api';
import { getFilenameFromHeaders } from '@/services/helpers';
import { PayoutTransactionClientData } from '@/types/Payouts';

import {
  AdhocBatchPreviewResult,
  BatchDocument,
  BatchGroupInfo,
  BatchQueryParams,
  CreateAdhocBatchPayload,
  CreatePayeeTransactionPayload,
  CreateProjectTransactionPayload,
  getProjectTransactionsQuery,
  getProjectTransactionsQueryOptions,
  InvoiceNeededInfo,
  PaginationElement,
  PayoutTransactionLevel,
  PreviewAdhocBatchParams,
  ProjectTransactionsSummary,
  ProjectTransactionStatus,
  ProjectWithPaymentPlanInfo,
} from 'types';

export const payoutsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getProjectByExternalId: build.query<ProjectWithPaymentPlanInfo, string>({
      query: (externalId) => ({ url: `payouts/projects/external/${externalId}` }),
      providesTags: (_result, _error, id) => [{ type: 'Projects', id }],
    }),
    getInvoiceNeededProjects: build.query<InvoiceNeededInfo[], void>({
      query: () => ({ url: `payouts/projects/invoices-needed` }),
      providesTags: (_result, _error, _args) => [{ type: 'Projects', id: `LIST` }],
    }),
    getProjectTransactionFilters: build.query<any, Record<any, any>>({
      query: (queryParams) => {
        return {
          url: `payouts/filters${
            queryParams &&
            '?' +
              new URLSearchParams({
                ...queryParams,
              }).toString()
          }`,
        };
      },
    }),
    getPayoutTransactions: build.query<
      {
        transactions: PayoutTransactionClientData[];
        pagination: PaginationElement;
      },
      getProjectTransactionsQuery & getProjectTransactionsQueryOptions
    >({
      query: (args) => ({ url: `payouts/transactions?${buildQueryString(args)}` }),
      // Mutations that only matter to organization rows invalidate PAYEE_LIST, so a payee-level list
      // has to provide it to be refetched by them.
      providesTags: (_result, _error, args) => [
        { type: 'PayoutTransactions', id: `LIST` },
        ...(args.transactionLevel === PayoutTransactionLevel.payee
          ? [{ type: 'PayoutTransactions' as const, id: 'PAYEE_LIST' }]
          : []),
      ],
      transformResponse: (response: any, meta: any) => {
        const headers = meta?.response?.headers;
        return {
          transactions: response,
          pagination: {
            count: parseInt(headers.get('X-Total-Count')),
            page: parseInt(headers.get('X-Page')),
            limit: parseInt(headers.get('X-Per-Page')),
            totalAmount: parseFloat(headers.get('X-Total-Amount')),
            totalPayees: parseInt(headers.get('X-Total-Payees')),
          },
        };
      },
    }),
    getProjectTransactionsByExternalId: build.query<
      {
        transactions: PayoutTransactionClientData[];
      },
      getProjectTransactionsQuery & Pick<getProjectTransactionsQueryOptions, 'sort'>
    >({
      query: ({ projectExternalId, ...rest }) => ({
        url: `payouts/projects/external/${projectExternalId}/transactions?${buildQueryString(rest)}`,
      }),
      providesTags: (_result, _error, args) => {
        return [{ type: 'PayoutTransactions', id: args.projectExternalId }];
      },
    }),

    getProjectTransactionsSummaryByExternalId: build.query<
      ProjectTransactionsSummary,
      { projectExternalId: string }
    >({
      query: ({ projectExternalId }) => ({
        url: `payouts/projects/external/${projectExternalId}/transactions/summary`,
      }),
      providesTags: (_result, _error, args) => {
        return [{ type: 'ProjectTransactionsSummary', id: args.projectExternalId }];
      },
    }),
    getBatchGroups: build.query<BatchGroupInfo[], BatchQueryParams>({
      query: (queryParams) => ({
        url: `payouts/batch-groups?${buildQueryString(queryParams)}`,
      }),
      providesTags: (_result, _error, _args) => {
        return [{ type: 'Batches', id: `LIST` }];
      },
    }),
    getBatchById: build.query<BatchDocument, { batchId: string }>({
      query: ({ batchId }) => ({
        url: `payouts/batches/${batchId}`,
      }),
      providesTags: (result, _error, _args) => {
        return [{ type: 'Batches', id: result?.id }];
      },
    }),
    approveBatches: build.mutation<void, { batchIds: string[] }>({
      query: ({ batchIds }) => ({ url: `payouts/batches/approve`, method: 'POST', body: { batchIds } }),
      invalidatesTags: [
        { type: 'PayoutTransactions', id: 'LIST' },
        { type: 'Batches', id: `LIST` },
      ],
    }),
    createAdhocBatch: build.mutation<{ batchIds: string[] }, CreateAdhocBatchPayload>({
      query: (body) => ({ url: `payouts/batches/adhoc`, method: 'POST', body }),
      invalidatesTags: [
        { type: 'PayoutTransactions', id: 'LIST' },
        { type: 'Batches', id: `LIST` },
      ],
    }),
    previewAdhocBatch: build.mutation<AdhocBatchPreviewResult, PreviewAdhocBatchParams>({
      query: (body) => ({ url: `payouts/batches/adhoc/preview`, method: 'POST', body }),
    }),
    recalculateBatchGroups: build.mutation<void, { batchIds: string[] }>({
      query: ({ batchIds }) => ({ url: `payouts/batches/recalculate`, method: 'POST', body: { batchIds } }),
      invalidatesTags: [
        { type: 'PayoutTransactions', id: 'LIST' },
        { type: 'Batches', id: `LIST` },
      ],
    }),
    unbatchTransaction: build.mutation<void, { transactionId: string }>({
      query: ({ transactionId }) => ({ url: `payouts/transactions/${transactionId}/unbatch`, method: 'POST' }),
      invalidatesTags: [
        { type: 'PayoutTransactions', id: 'LIST' },
        { type: 'Batches', id: `LIST` },
      ],
    }),
    addTransactionToBatch: build.mutation<void, { transactionIds: string[] }>({
      query: ({ transactionIds }) => ({
        url: `payouts/transactions/add-to-batch`,
        method: 'POST',
        body: { transactionIds },
      }),
      invalidatesTags: [
        { type: 'PayoutTransactions', id: 'LIST' },
        { type: 'Batches', id: `LIST` },
      ],
    }),
    createProjectTransaction: build.mutation<void, CreateProjectTransactionPayload>({
      query: (data) => ({ url: `payouts/transactions`, method: 'POST', body: data }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'PayoutTransactions', id: 'LIST' },
        { type: 'PayoutTransactions', id: arg.projectExternalId },
        { type: 'ProjectTransactionsSummary', id: arg.projectExternalId },
        { type: 'Batches', id: `LIST` },
      ],
    }),
    createPayeeTransaction: build.mutation<void, CreatePayeeTransactionPayload>({
      query: (body) => ({ url: `payouts/payee-transactions`, method: 'POST', body }),
      // The payee list is cached under its own id, so without it the new row does not show up.
      invalidatesTags: [
        { type: 'PayoutTransactions', id: 'LIST' },
        { type: 'PayoutTransactions', id: 'PAYEE_LIST' },
      ],
    }),
    importProjectTransactionsCsv: build.mutation<{ csv: string; fileName: string }, File>({
      query: (file) => {
        const body = new FormData();
        body.append('file', file);
        return {
          url: `payouts/transactions/csv-import`,
          method: 'POST',
          body,
          // Text keeps the results serializable in the store; a 400 stays JSON so its message and errors can be shown.
          responseHandler: (response) => (response.ok ? response.text() : response.json()),
        };
      },
      transformResponse: (csv: string, meta, file) => ({
        csv,
        fileName: getFilenameFromHeaders(
          meta?.response?.headers as Headers,
          `${file.name.replace(/\.csv$/i, '')}-results.csv`,
        ),
      }),
      // A rejected file writes nothing, so only a processed one refreshes the list.
      invalidatesTags: (result) => (result ? [{ type: 'PayoutTransactions', id: 'LIST' }] : []),
    }),
    deleteProjectTransaction: build.mutation<{ projectExternalId: string }, { transactionId: string }>({
      query: ({ transactionId }) => ({ url: `payouts/transactions/${transactionId}`, method: 'DELETE' }),
      invalidatesTags: (result) => [
        { type: 'PayoutTransactions', id: 'LIST' },
        // The payee list is cached under its own id, so without this the row stays on screen after
        // a delete that succeeded. The optimistic patch below only touches getPayoutTransactions.
        { type: 'PayoutTransactions', id: 'PAYEE_LIST' },
        { type: 'PayoutTransactions', id: result?.projectExternalId || '' },
        { type: 'ProjectTransactionsSummary', id: result?.projectExternalId || '' },
        { type: 'Batches', id: `LIST` },
      ],
      onQueryStarted: async ({ transactionId }, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          payoutsApi.util.updateQueryData('getPayoutTransactions', {}, (draft) => {
            draft.transactions = draft.transactions.filter((t) => t.id !== transactionId);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateProjectPaymentPlan: build.mutation<void, { externalId: string; planId: string }>({
      query: ({ externalId, planId }) => ({
        url: `payouts/project/${externalId}/plan`,
        method: 'PATCH',
        body: { planId, externalId },
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: 'ProjectTransactionsSummary', id: arg.externalId },
        { type: 'PayoutTransactions', id: arg.externalId },
        { type: 'PayoutTransactions', id: 'LIST' },
      ],
    }),
    updateProjectTransactionStatus: build.mutation<
      { projectExternalId: string },
      {
        transactionId: string;
        status: ProjectTransactionStatus;
        pauseExpirationDate?: string;
        pauseReason?: string;
      }
    >({
      query: ({ transactionId, status, pauseExpirationDate, pauseReason }) => ({
        url: `payouts/transactions/${transactionId}/status`,
        method: 'PUT',
        body: { status, pauseExpirationDate, pauseReason },
      }),
      invalidatesTags: (result) => [
        { type: 'PayoutTransactions', id: 'LIST' },
        { type: 'PayoutTransactions', id: 'PAYEE_LIST' },
        { type: 'PayoutTransactions', id: result?.projectExternalId || '' },
        { type: 'ProjectTransactionsSummary', id: result?.projectExternalId || '' },
        { type: 'Batches', id: `LIST` },
      ],
      async onQueryStarted({ transactionId, status }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          payoutsApi.util.updateQueryData('getPayoutTransactions', {}, (draft) => {
            const tx = draft.transactions.find((t) => t.id === transactionId);
            if (tx) {
              tx.status = status;
            }
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getPayeeByExternalId: build.query<any, string>({
      query: (externalId) => ({ url: `payouts/payees/${externalId}` }),
      providesTags: (_result, _error, id) => [{ type: 'Payees', id }],
    }),
  }),
});

export const {
  useGetProjectByExternalIdQuery,
  useGetInvoiceNeededProjectsQuery,
  useGetPayoutTransactionsQuery,
  useLazyGetPayoutTransactionsQuery,
  useGetProjectTransactionFiltersQuery,
  useApproveBatchesMutation,
  useCreateAdhocBatchMutation,
  usePreviewAdhocBatchMutation,
  useRecalculateBatchGroupsMutation,
  useGetBatchGroupsQuery,
  useUnbatchTransactionMutation,
  useAddTransactionToBatchMutation,
  useCreateProjectTransactionMutation,
  useCreatePayeeTransactionMutation,
  useImportProjectTransactionsCsvMutation,
  useDeleteProjectTransactionMutation,
  useUpdateProjectPaymentPlanMutation,
  useGetBatchByIdQuery,
  useUpdateProjectTransactionStatusMutation,
  useGetPayeeByExternalIdQuery,
  useGetProjectTransactionsByExternalIdQuery,
  useLazyGetProjectTransactionsByExternalIdQuery,
  useGetProjectTransactionsSummaryByExternalIdQuery,
} = payoutsApi;
